import { tz } from '@date-fns/tz'
import { parseLocalDate } from '@utils/helpers/src/date'
import {
  type Duration,
  format as formatDateFns,
  formatDuration as formatDurationFns,
} from 'date-fns'
import { de } from 'date-fns/locale/de'
import { DEFAULT_TIME_ZONE } from '@/lib/time-zone'

export const GERMAN_LOCALE = 'de'
export type DateInput = Date | number | string

// Offset-less strings are wall time in the requested timezone. Other inputs are instants.
function parseDateInput(date: DateInput, timeZone = DEFAULT_TIME_ZONE): Date | number {
  return typeof date === 'string' ? parseLocalDate({ local: date, timezone: timeZone }) : date
}

function formatInTimeZone(date: DateInput, pattern: string, timeZone: string) {
  return formatDateFns(parseDateInput(date, timeZone), pattern, { in: tz(timeZone), locale: de })
}

/** 01.07.2026 */
export const formatDate = (date: DateInput, timeZone = DEFAULT_TIME_ZONE): string =>
  formatInTimeZone(date, 'P', timeZone)

/** 01.07.26 */
export const formatDateWithShortYear = (date: DateInput, timeZone = DEFAULT_TIME_ZONE): string =>
  formatInTimeZone(date, 'dd.MM.yy', timeZone)

/** Mittwoch, 01.07.2026 */
export const formatDateWithWeekday = (date: DateInput, timeZone = DEFAULT_TIME_ZONE): string =>
  formatInTimeZone(date, 'EEEE, P', timeZone)

/** Mi., 01.07.2026 */
export const formatDateWithShortWeekday = (date: DateInput, timeZone = DEFAULT_TIME_ZONE): string =>
  formatInTimeZone(date, 'EEE, P', timeZone)

/** Mi, 01.07.2026 */
export const formatDateWithCompactWeekday = (
  date: DateInput,
  timeZone = DEFAULT_TIME_ZONE
): string => formatInTimeZone(date, 'EEEEEE, P', timeZone)

/** 01.07. */
export const formatDateWithoutYear = (date: DateInput, timeZone = DEFAULT_TIME_ZONE): string =>
  formatInTimeZone(date, 'dd.MM.', timeZone)

/** 07.07. bis 16.07.2026 */
export const formatDateRange = (
  from: DateInput,
  to: DateInput,
  timeZone = DEFAULT_TIME_ZONE
): string => {
  const sameYear =
    formatInTimeZone(from, 'yyyy', timeZone) === formatInTimeZone(to, 'yyyy', timeZone)
  const start = sameYear ? formatDateWithoutYear(from, timeZone) : formatDate(from, timeZone)

  return `${start} bis ${formatDate(to, timeZone)}`
}

/** 14:00 */
export const formatTime = (date: DateInput, timeZone = DEFAULT_TIME_ZONE): string =>
  formatInTimeZone(date, 'p', timeZone)

/** Juli 2026 */
export const formatMonthYear = (date: DateInput, timeZone = DEFAULT_TIME_ZONE): string =>
  formatInTimeZone(date, 'MMMM yyyy', timeZone)

/** Jul */
export const formatShortMonth = (date: DateInput, timeZone = DEFAULT_TIME_ZONE): string =>
  formatInTimeZone(date, 'LLL', timeZone)

/** Mittwoch */
export const formatWeekday = (date: DateInput, timeZone = DEFAULT_TIME_ZONE): string =>
  formatInTimeZone(date, 'EEEE', timeZone)

/** 2026-07-01 */
export const formatIsoDate = (date: DateInput, timeZone = DEFAULT_TIME_ZONE): string =>
  formatInTimeZone(date, 'yyyy-MM-dd', timeZone)

export function formatDuration(
  duration: Duration,
  options?: { delimiter?: string; zero?: boolean }
): string {
  return formatDurationFns(duration, { zero: true, ...options, locale: de })
}

// Configurable labels, such as datepicker ranges, still accept Intl options.
export function formatIntl(options: Intl.DateTimeFormatOptions): (date: DateInput) => string
export function formatIntl(options: Intl.DateTimeFormatOptions, date: DateInput): string
export function formatIntl(options: Intl.DateTimeFormatOptions, date?: DateInput) {
  const timeZone = options.timeZone ?? DEFAULT_TIME_ZONE
  const formatter = new Intl.DateTimeFormat(GERMAN_LOCALE, {
    timeZone: DEFAULT_TIME_ZONE,
    ...options,
  })
  const format = (value: DateInput) => formatter.format(parseDateInput(value, timeZone))
  return date === undefined ? format : format(date)
}

export const createGermanRelativeTimeFormatter = (options?: Intl.RelativeTimeFormatOptions) =>
  new Intl.RelativeTimeFormat(GERMAN_LOCALE, options)
